import { useState, useEffect, useMemo } from "react";
import {
  ArrowLeft,
  Heart,
  ShoppingCart,
  FileText,
  Plus,
  Minus,
  Download,
  Check,
  Package,
  ChevronRight,
  Box,
  Truck,
  MapPin,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import { t } from "../data/translations";
import {
  Product,
  getCategoryLabel,
  SUBCATEGORY_LABELS,
  SECTOR_LABELS,
} from "../data/products";
import { productsApi, transformProduct } from "../api/products";
import ProductCard from "../components/ProductCard";

export default function ProductPage() {
  const {
    lang,
    setPage,
    addToCart,
    toggleFavorite,
    favorites,
    setCatalogueFilter,
  } = useApp();
  const T = t[lang];

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [qty, setQty] = useState(1);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [added, setAdded] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [recommendedProducts, setRecommendedProducts] = useState<Product[]>([]);
  const [loadingRecommended, setLoadingRecommended] = useState(false);
  const [pageVisible, setPageVisible] = useState(false);

  const productSlug = useMemo(() => {
    const pathParts = window.location.pathname.split("/");
    return pathParts[pathParts.length - 1];
  }, []);

  useEffect(() => {
    if (!productSlug) {
      setError(T.product.notFound);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    productsApi
      .getProduct(productSlug)
      .then((apiProduct) => {
        const transformed = transformProduct(apiProduct);
        setProduct(transformed);
        setQty(transformed.moq || 1);
        if (typeof document !== "undefined") {
          const productName =
            lang === "fr" ? transformed.name : transformed.nameEn;
          document.title = `${productName} — LODISS`;
          const metaDesc = document.querySelector('meta[name="description"]');
          if (metaDesc) {
            const desc =
              lang === "fr"
                ? transformed.description
                : transformed.descriptionEn;
            metaDesc.setAttribute("content", (desc || "").slice(0, 160));
          }
        }
        setTimeout(() => setPageVisible(true), 50);
      })
      .catch((err) => {
        console.error("Failed to fetch product:", err);
        setError(T.product.errorLoading);
      })
      .finally(() => setLoading(false));
  }, [productSlug, lang, T.product.notFound, T.product.errorLoading]);

  useEffect(() => {
    if (product && product.category) {
      setLoadingRecommended(true);
      productsApi
        .getProducts({ category: product.category, limit: 5 })
        .then((data) => {
          const transformed = data.products.map(transformProduct);
          const filtered = transformed
            .filter((p) => p.id !== product.id)
            .slice(0, 4);
          setRecommendedProducts(filtered);
        })
        .catch(() => setRecommendedProducts([]))
        .finally(() => setLoadingRecommended(false));
    }
  }, [product]);

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-lodiss-red border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-400">
            {lang === "fr" ? "Chargement..." : "Loading..."}
          </p>
        </div>
      </main>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (error || !product) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center px-4">
          <Package size={48} className="mx-auto text-gray-200 mb-4" />
          <h1 className="text-lg font-bold text-gray-800 mb-2">
            {error || T.product.notFound}
          </h1>
          <p className="text-gray-500 text-sm mb-6">
            {lang === "fr"
              ? "Le produit demandé est introuvable ou a été retiré."
              : "The requested product could not be found or has been removed."}
          </p>
          <button
            onClick={() => {
              window.location.href = "/products";
            }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-lodiss-red text-white text-sm font-semibold rounded-lg hover:bg-lodiss-dark transition-colors"
          >
            <ArrowLeft size={16} />
            {lang === "fr" ? "Retour au catalogue" : "Back to catalogue"}
          </button>
        </div>
      </main>
    );
  }

  // ── Derived data ───────────────────────────────────────────────────────────
  const isQuote = qty >= product.quoteThreshold;
  const isFav = favorites.includes(product.id);
  const name = lang === "fr" ? product.name : product.nameEn;
  const description =
    lang === "fr" ? product.description : product.descriptionEn;
  const features = lang === "fr" ? product.features : product.featuresEn;
  const categoryLabel = getCategoryLabel(product.category, lang);
  const subcategoryLabel =
    SUBCATEGORY_LABELS[product.subcategory]?.[lang] || "";
  const sectorLabels = product.sector
    .map((s) => SECTOR_LABELS[s]?.[lang])
    .filter(Boolean);

  const images =
    product.images && product.images.length > 0
      ? product.images
      : ([] as string[]);

  const stockMeta = () => {
    if (product.stock === 0)
      return {
        label: T.bestsellers.outStock,
        cls: "text-red-600 bg-red-50 border border-red-100",
        dot: "bg-red-500",
      };
    if (product.stock < 20)
      return {
        label: T.bestsellers.lowStock,
        cls: "text-amber-600 bg-amber-50 border border-amber-100",
        dot: "bg-amber-500",
      };
    return {
      label: T.bestsellers.inStock,
      cls: "text-emerald-600 bg-emerald-50 border border-emerald-100",
      dot: "bg-emerald-500",
    };
  };
  const stock = stockMeta();

  const handleAction = () => {
    addToCart({ product, qty, selectedSize, selectedColor });
    if (isQuote) {
      setPage("quote-request");
    } else {
      setAdded(true);
      setTimeout(() => setAdded(false), 2000);
    }
  };

  const handleCategoryClick = () => {
    setCatalogueFilter(product.category);
    window.location.href = `/products?category=${product.category}`;
  };

  const descShort = (description || "").length > 200 && !showFullDescription;

  return (
    <main
      className="bg-white min-h-screen"
      style={{
        opacity: pageVisible ? 1 : 0,
        transform: pageVisible ? "translateY(0)" : "translateY(20px)",
        transition: "opacity 0.4s ease, transform 0.4s ease",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* ── ZONE 1 — Back + Breadcrumb ──────────────────────────────────── */}
        <div className="mb-6">
          <button
            onClick={() => {
              window.location.href = "/products";
            }}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-lodiss-red mb-3 transition-colors group"
          >
            <ArrowLeft
              size={16}
              className="group-hover:-translate-x-0.5 transition-transform"
            />
            {T.common.back}
          </button>

          <nav
            aria-label="breadcrumb"
            className="flex items-center gap-1 text-xs text-gray-400"
          >
            <button
              onClick={() => {
                window.location.href = "/";
              }}
              className="hover:text-gray-600 transition-colors"
            >
              {T.product.home}
            </button>
            <ChevronRight size={11} className="text-gray-300" />
            <button
              onClick={handleCategoryClick}
              className="hover:text-gray-600 transition-colors"
            >
              {T.product.catalogue}
            </button>
            <ChevronRight size={11} className="text-gray-300" />
            <span className="text-gray-500 truncate max-w-[220px] font-medium">
              {name}
            </span>
          </nav>
        </div>

        {/* ── ZONE 2 — Two-column layout ──────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 mb-16">
          {/* Left — Image gallery */}
          <div className="space-y-3">
            {/* Main image */}
            <div className="aspect-square rounded-2xl overflow-hidden bg-gray-50 border border-gray-100 relative">
              {images.length > 0 ? (
                <img
                  key={selectedImageIndex}
                  src={images[selectedImageIndex]}
                  alt={name}
                  className="w-full h-full object-cover"
                  style={{ animation: "fadeIn 0.25s ease" }}
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 p-8">
                  <img
                    src="/logo-1762256791_-_Modifie.png"
                    alt="Lodiss"
                    className="w-1/3 opacity-20 grayscale object-contain"
                  />
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImageIndex(idx)}
                    className={`w-16 h-16 rounded-xl overflow-hidden border-2 flex-shrink-0 transition-all duration-200 ${
                      idx === selectedImageIndex
                        ? "border-lodiss-red shadow-sm"
                        : "border-transparent hover:border-gray-200"
                    }`}
                  >
                    <img
                      src={img}
                      alt={`${name} ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right — Product info */}
          <div className="flex flex-col">
            {/* 1. Header */}
            <div className="mb-4">
              <p className="text-xs font-mono uppercase tracking-wider text-gray-400 mb-1">
                {product.ref} {product.brand}
              </p>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight mb-2">
                {name}
              </h1>
              {subcategoryLabel && (
                <p className="text-[11px] text-gray-400">
                  {categoryLabel} › {subcategoryLabel}
                </p>
              )}
            </div>

            {/* 2. Status badges */}
            <div className="flex flex-wrap items-center gap-2 mb-5">
              <span
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${stock.cls}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${stock.dot}`} />
                {stock.label}
              </span>

              {(product.norm || []).slice(0, 3).map((n, i) => (
                <span
                  key={i}
                  className="text-[11px] text-gray-500 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded font-mono"
                >
                  {n}
                </span>
              ))}

              {product.badge && (
                <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-100 px-2.5 py-1 rounded-full">
                  {lang === "fr" ? product.badge : product.badgeEn}
                </span>
              )}

              {sectorLabels.slice(0, 3).map((sector, i) => (
                <span
                  key={i}
                  className="text-[10px] text-gray-500 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-full"
                >
                  {sector}
                </span>
              ))}
            </div>

            {/* 3. Price & commercial conditions */}
            <div className="mb-5 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-gray-900">
                  {Number(product.price).toFixed(3)}
                </span>
                <span className="text-sm text-gray-500">DT {T.common.ht}</span>
              </div>
              {product.moq > 1 && (
                <p className="text-sm text-gray-500 mt-1.5">
                  {T.product.minQty} :{" "}
                  <span className="font-semibold text-gray-700">
                    {product.moq}
                  </span>
                </p>
              )}
              {product.quoteThreshold > 1 && (
                <div className="mt-2.5 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 px-3 py-2 rounded-lg">
                  <FileText size={13} className="flex-shrink-0 mt-0.5" />
                  <span>
                    {lang === "fr"
                      ? `Au-delà de ${product.quoteThreshold} unités, ce produit passe en mode devis — tarifs dégressifs sur demande.`
                      : `Beyond ${product.quoteThreshold} units, this product switches to quote mode — tiered pricing on request.`}
                  </span>
                </div>
              )}
            </div>

            {/* 4. Description */}
            {description && (
              <div className="mb-5">
                <p className="text-sm text-gray-600 leading-relaxed">
                  {descShort ? (
                    <>
                      {description.slice(0, 200)}…{" "}
                      <button
                        onClick={() => setShowFullDescription(true)}
                        className="text-lodiss-red font-medium hover:underline"
                      >
                        {T.product.seeMore}
                      </button>
                    </>
                  ) : (
                    <>
                      {description}
                      {description.length > 200 && (
                        <button
                          onClick={() => setShowFullDescription(false)}
                          className="text-lodiss-red font-medium ml-1 hover:underline"
                        >
                          {" "}
                          {T.product.seeLess}
                        </button>
                      )}
                    </>
                  )}
                </p>
              </div>
            )}

            {/* 5. Variants */}
            {(product.variants?.sizes || []).length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {lang === "fr" ? "Taille" : "Size"}
                </label>
                <div className="flex flex-wrap gap-2">
                  {(product.variants.sizes || []).map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`px-4 py-2 text-sm border rounded-lg transition-all ${
                        selectedSize === size
                          ? "border-lodiss-red bg-red-50 text-lodiss-red font-medium"
                          : "border-gray-200 text-gray-600 hover:border-gray-300"
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
                {selectedSize && (
                  <p className="text-xs text-gray-500 mt-1.5">
                    {T.product.selectedSize} :{" "}
                    <span className="font-medium text-gray-700">
                      {selectedSize}
                    </span>
                  </p>
                )}
              </div>
            )}

            {(product.variants?.colors || []).length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {lang === "fr" ? "Couleur" : "Color"}
                </label>
                <div className="flex flex-wrap gap-2">
                  {(product.variants.colors || []).map((color) => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`px-4 py-2 text-sm border rounded-lg transition-all ${
                        selectedColor === color
                          ? "border-lodiss-red bg-red-50 text-lodiss-red font-medium"
                          : "border-gray-200 text-gray-600 hover:border-gray-300"
                      }`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
                {selectedColor && (
                  <p className="text-xs text-gray-500 mt-1.5">
                    {T.product.selectedColor} :{" "}
                    <span className="font-medium text-gray-700">
                      {selectedColor}
                    </span>
                  </p>
                )}
              </div>
            )}

            {/* 6. Quantity selector + action */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {T.product.qty}
              </label>
              <div className="flex items-center gap-3">
                {/* Counter */}
                <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setQty(Math.max(product.moq || 1, qty - 1))}
                    className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Minus size={15} />
                  </button>
                  <input
                    type="number"
                    value={qty}
                    onChange={(e) =>
                      setQty(
                        Math.max(
                          product.moq || 1,
                          parseInt(e.target.value) || 1,
                        ),
                      )
                    }
                    className="w-14 text-center text-sm font-semibold border-none focus:outline-none focus:ring-0 bg-transparent"
                  />
                  <button
                    onClick={() => setQty(qty + 1)}
                    className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Plus size={15} />
                  </button>
                </div>

                {/* Main action button */}
                <button
                  onClick={handleAction}
                  disabled={product.stock === 0}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-semibold text-sm transition-all ${
                    product.stock === 0
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : isQuote
                        ? "bg-amber-500 hover:bg-amber-600 text-white shadow-sm hover:shadow"
                        : added
                          ? "bg-emerald-600 text-white"
                          : "bg-lodiss-red hover:bg-lodiss-dark text-white shadow-sm hover:shadow"
                  }`}
                >
                  {isQuote ? (
                    <>
                      <FileText size={16} />
                      {T.common.requestQuote}
                    </>
                  ) : added ? (
                    <>
                      <Check size={16} />
                      {lang === "fr" ? "Ajouté !" : "Added!"}
                    </>
                  ) : (
                    <>
                      <ShoppingCart size={16} />
                      {T.common.addToCart}
                    </>
                  )}
                </button>

                {/* Favourite */}
                <button
                  onClick={() => toggleFavorite(product.id)}
                  className={`w-10 h-10 rounded-xl border flex items-center justify-center transition-all ${
                    isFav
                      ? "border-lodiss-red/30 bg-red-50 text-lodiss-red"
                      : "border-gray-200 text-gray-400 hover:text-lodiss-red hover:border-lodiss-red/30 hover:bg-red-50"
                  }`}
                >
                  <Heart size={17} fill={isFav ? "currentColor" : "none"} />
                </button>
              </div>

              {/* Contextual messages */}
              {isQuote && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <FileText size={11} />
                  {T.product.quoteRedirectMsg}
                </p>
              )}
            </div>

            {/* 7. Delivery + downloads */}
            <div className="border-t border-gray-100 pt-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                  <MapPin size={15} className="text-gray-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700">
                    {lang === "fr" ? "Livraison" : "Delivery"}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {T.product.inTunisiaMaghreb}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 border-t border-gray-50 pt-3">
                <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                  <Download size={15} className="text-gray-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700">
                    {T.product.downloads}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {T.product.techSheetsCerts}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── ZONE 3 — Features / Caractéristiques ────────────────────────── */}
        {features && features.length > 0 && (
          <div className="mb-16">
            <h2 className="text-xl font-bold text-gray-900 mb-5">
              {T.product.features}
            </h2>

            {(product.norm || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-5">
                {product.norm.map((n, i) => (
                  <span
                    key={i}
                    className="px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg font-mono tracking-wide"
                  >
                    {n}
                  </span>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {features.map((feature, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-default"
                >
                  <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={11} className="text-emerald-600" />
                  </div>
                  <span className="text-sm text-gray-700 leading-snug">
                    {feature}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── ZONE 4 — Technical table ─────────────────────────────────────── */}
        <div className="mb-16">
          <h2 className="text-xl font-bold text-gray-900 mb-5">
            {T.product.technicalInfo}
          </h2>
          <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <tbody>
                <tr className="bg-gray-50/80 hover:bg-gray-100 transition-colors border-b border-gray-100">
                  <td className="py-3.5 px-5 text-gray-500 font-medium w-1/3">
                    {T.product.reference}
                  </td>
                  <td className="py-3.5 px-5 font-mono text-gray-800 text-xs">
                    {product.ref}
                  </td>
                </tr>
                <tr className="bg-white hover:bg-gray-50 transition-colors border-b border-gray-100">
                  <td className="py-3.5 px-5 text-gray-500 font-medium">
                    {T.product.brand}
                  </td>
                  <td className="py-3.5 px-5 text-gray-800">{product.brand}</td>
                </tr>
                <tr className="bg-gray-50/80 hover:bg-gray-100 transition-colors border-b border-gray-100">
                  <td className="py-3.5 px-5 text-gray-500 font-medium">
                    {T.product.category}
                  </td>
                  <td className="py-3.5 px-5 text-gray-800">{categoryLabel}</td>
                </tr>
                {subcategoryLabel && (
                  <tr className="bg-white hover:bg-gray-50 transition-colors border-b border-gray-100">
                    <td className="py-3.5 px-5 text-gray-500 font-medium">
                      {T.product.subcategory}
                    </td>
                    <td className="py-3.5 px-5 text-gray-800">
                      {subcategoryLabel}
                    </td>
                  </tr>
                )}
                {sectorLabels.length > 0 && (
                  <tr className="bg-gray-50/80 hover:bg-gray-100 transition-colors border-b border-gray-100">
                    <td className="py-3.5 px-5 text-gray-500 font-medium">
                      {T.product.sectors}
                    </td>
                    <td className="py-3.5 px-5 text-gray-800">
                      {sectorLabels.join(", ")}
                    </td>
                  </tr>
                )}
                {(product.norm || []).length > 0 && (
                  <tr className="bg-white hover:bg-gray-50 transition-colors border-b border-gray-100">
                    <td className="py-3.5 px-5 text-gray-500 font-medium">
                      {T.product.standards}
                    </td>
                    <td className="py-3.5 px-5">
                      <div className="flex flex-wrap gap-1">
                        {product.norm.map((n, i) => (
                          <span
                            key={i}
                            className="px-2 py-0.5 bg-gray-100 border border-gray-200 text-gray-600 text-xs rounded font-mono"
                          >
                            {n}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
                <tr className="bg-gray-50/80 hover:bg-gray-100 transition-colors border-b border-gray-100">
                  <td className="py-3.5 px-5 text-gray-500 font-medium">
                    {T.product.availability}
                  </td>
                  <td className="py-3.5 px-5">
                    <span
                      className={`font-medium ${product.stock > 0 ? "text-emerald-600" : "text-red-600"}`}
                    >
                      {product.stock > 0
                        ? T.bestsellers.inStock
                        : T.bestsellers.outStock}
                    </span>
                  </td>
                </tr>
                <tr className="bg-white hover:bg-gray-50 transition-colors">
                  <td className="py-3.5 px-5 text-gray-500 font-medium">
                    {T.product.moqLabel}
                  </td>
                  <td className="py-3.5 px-5 text-gray-800">
                    {product.moq}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* ── ZONE 5 — Related products ────────────────────────────────────── */}
        {(recommendedProducts.length > 0 || loadingRecommended) && (
          <div className="border-t border-gray-100 pt-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {T.product.sameCategory}
              </h2>
              <button
                onClick={handleCategoryClick}
                className="text-sm text-lodiss-red font-medium hover:underline flex items-center gap-1"
              >
                {T.product.viewAll}
                <ChevronRight size={15} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {loadingRecommended
                ? Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="bg-white border border-gray-100 rounded-xl overflow-hidden"
                    >
                      <div className="aspect-[4/3] bg-gray-100 animate-pulse" />
                      <div className="p-4 space-y-3">
                        <div className="h-3 bg-gray-100 rounded animate-pulse w-3/4" />
                        <div className="h-4 bg-gray-100 rounded animate-pulse w-1/2" />
                        <div className="h-7 bg-gray-100 rounded animate-pulse w-1/3" />
                      </div>
                    </div>
                  ))
                : recommendedProducts.map((p) => (
                    <ProductCard key={p.id} product={p} />
                  ))}
            </div>
          </div>
        )}
      </div>

      {/* Inline keyframes for image fade */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </main>
  );
}
